// src/components/Home/Home.js
import { Link } from "react-router-dom";
import { Auth, Hub } from "aws-amplify";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { Button } from "@mui/material";
import { fetchData, postQuery } from "../../services/BackendServices";
import { useLoading } from "../../context/LoadingContext";
import AlertModal from "../../pages/Requirement/components/common/AlerModel";

const roleGroupMap = {
  agropreneur: "AMTPL_AGROPRENEURS",
  "global-partner": "AMTPL_GLOBAL_PARTNERS",
  "seller-farmer": "AMTPL_SELLERS_AND_FARMERS",
  buyer: "AMTPL_BUYERS",
  amtpl: "AMTPL_ADMINS",
};

// Helper function to determine role from user groups
const getUserRoleFromGroups = (userGroups) => {
  for (const [role, group] of Object.entries(roleGroupMap)) {
    if (userGroups.includes(group)) {
      return role;
    }
  }
  return null;
};

const Home = () => {
  const navigate = useNavigate();
  const { setLoading } = useLoading();

  // Modal state for alerts
  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    autoClose: false,
  });

  // Handle modal close
  const handleModalClose = () => {
    setModal((prev) => ({ ...prev, show: false }));
  };

  // Helper function to clear session and localStorage
  const clearSessionAndStorage = async () => {
    try {
      // Clear localStorage
      localStorage.removeItem("pendingOAuthRole");
      localStorage.clear();

      // Sign out from AWS Cognito
      await Auth.signOut({ global: true });

      /* console.log("Session and localStorage cleared"); */
    } catch (error) {
      console.error("Error clearing session:", error);
    }
  };

  // Handle OAuth and authentication status
  useEffect(() => {
    // Handle OAuth callback manually if Amplify doesn't catch it
    const handleManualOAuthCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get("code");
      const state = urlParams.get("state");
      const error = urlParams.get("error");
      const errorDescription = urlParams.get("error_description");

      if (error) {
        console.error(" OAuth error:", error, errorDescription);
        localStorage.removeItem("pendingOAuthRole");
        setLoading(false);
        setModal({
          show: true,
          type: "error",
          title: "Authentication Failed",
          message:
            errorDescription || "Failed to authenticate. Please try again.",
          autoClose: false,
        });
        // Clean URL
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
        return;
      }

      if (code) {
        /* console.log(
          "Manual OAuth callback detected, code:",
          code.substring(0, 10) + "..."
        ); */
        /* console.log("State:", state); */
        setLoading(true);

        // Let Amplify handle the code exchange automatically
        // It should detect the code parameter in the URL
        /* console.log("Waiting for Amplify to exchange code for tokens..."); */
        await new Promise((resolve) => setTimeout(resolve, 4000));

        // Clean URL parameters after Amplify processes them
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      }
    };

    handleManualOAuthCallback();

    // Add Hub listener for OAuth events
    const hubListener = Hub.listen("auth", ({ payload: { event, data } }) => {
      /* console.log("Auth Hub event in Home:", event, data); */

      if (event === "signIn" || event === "cognitoHostedUI") {
        /* console.log("OAuth sign-in detected in Home.js"); */
        // Trigger a re-check of authentication status
        checkAuthStatus();
      } else if (
        event === "signIn_failure" ||
        event === "cognitoHostedUI_failure"
      ) {
        console.error("OAuth sign-in failed in Home.js:", data);
        localStorage.removeItem("pendingOAuthRole");
        setLoading(false);
        setModal({
          show: true,
          type: "error",
          title: "Authentication Failed",
          message: data?.message || "Failed to authenticate. Please try again.",
          autoClose: false,
        });
      }
    });

    // Check if user is already authenticated (useful after OAuth redirect from auth-dev.grainsglobaldev.com)
    const checkAuthStatus = async () => {
      try {
        // Check if this is a redirect from OAuth (common query params: code, state)
        const urlParams = new URLSearchParams(window.location.search);
        const hasOAuthParams = urlParams.has("code") || urlParams.has("state");
        const pendingRole = localStorage.getItem("pendingOAuthRole");
        console.log("hasOAuthParams:", hasOAuthParams);
        /* console.log("Pending OAuth role:", pendingRole, urlParams); */
        /* console.log("Current URL:", window.location.href); */

        // If we have a pending OAuth role but no OAuth params,
        // it means the OAuth callback already happened and URL was cleaned
        // We should still try to get the authenticated user
        if (hasOAuthParams || pendingRole) {
          /* console.log(
            " OAuth flow detected (params or pending role), waiting for authentication..."
          ); */
          setLoading(true);
          // Give Amplify more time to complete the OAuth flow
          await new Promise((resolve) => setTimeout(resolve, 3000));
        }

        /* console.log(" Checking authentication status on Home page..."); */

        // Try to get authenticated user with retries for OAuth flows
        let user = null;
        let attempts = 0;
        const maxAttempts = pendingRole ? 10 : 1; // Increased to 5 attempts

        while (attempts < maxAttempts && !user) {
          try {
            attempts++;
            /* console.log(`Authentication attempt ${attempts}/${maxAttempts}`); */
            user = await Auth.currentAuthenticatedUser({ bypassCache: true });
            /* console.log("User authenticated successfully"); */
            /* console.log("User details:", {
              username: user.username,
              email: user.attributes?.email,
              groups:
                user.signInUserSession?.idToken?.payload?.["cognito:groups"],
            }); */
          } catch (error) {
            /* console.log(`Attempt ${attempts} failed:`, error?.message || error); */
            if (attempts < maxAttempts) {
              /* console.log(`Waiting 3 seconds before retry...`); */
              await new Promise((resolve) => setTimeout(resolve, 3000)); // Increased to 3 seconds
            }
          }
        }

        if (!user) {
          console.error("User authentication failed after all retries");
          throw new Error("User authentication failed after retries");
        }

        if (user && user.signInUserSession && user.signInUserSession.idToken) {
          /* console.log(
            " User is authenticated, checking groups and redirecting..."
          ); */
          const idToken = user.signInUserSession.idToken.jwtToken;
          const decodedToken = jwtDecode(idToken);
          const userGroups = decodedToken["cognito:groups"] || [];

          console.log(" User groups found:", userGroups);
          console.log(" Pending OAuth role from localStorage:", pendingRole);
          console.log(" hasOAuthParams:", hasOAuthParams);

          // Check if user is trying to login with wrong role
          if (pendingRole && hasOAuthParams) {
            const hasBuyerGroup = userGroups.includes("AMTPL_BUYERS");
            const hasGPGroup = userGroups.includes("AMTPL_GLOBAL_PARTNERS");
            const requestedGroup = roleGroupMap[pendingRole];

            // User already has AMTPL_BUYERS but trying to login as global-partner
            if (hasBuyerGroup && requestedGroup === "AMTPL_GLOBAL_PARTNERS") {
              /* console.log(
                " User has AMTPL_BUYERS but trying to login as global-partner"
              ); */
              setLoading(false);
              localStorage.removeItem("pendingOAuthRole");
              await clearSessionAndStorage();
              setModal({
                show: true,
                type: "error",
                title: "Wrong Login Portal",
                message:
                  "You are registered as a Buyer. Please login through the Buyer login page instead of the Trading Partner login page.",
                autoClose: false,
              });
              return;
            }

            // User already has AMTPL_GLOBAL_PARTNERS but trying to login as buyer
            if (hasGPGroup && requestedGroup === "AMTPL_BUYERS") {
              /* console.log(
                " User has AMTPL_GLOBAL_PARTNERS but trying to login as buyer"
              ); */
              setLoading(false);
              localStorage.removeItem("pendingOAuthRole");
              await clearSessionAndStorage();
              setModal({
                show: true,
                type: "error",
                title: "Wrong Login Portal",
                message:
                  "You are registered as a Trading Partner. Please login through the Trading Partner login page instead of the Buyer login page.",
                autoClose: false,
              });
              return;
            }
          }

          // If user has no groups but there's a pending role from OAuth, assign the group
          if (
            !["AMTPL_GLOBAL_PARTNERS", "AMTPL_BUYERS"].some((role) =>
              userGroups.includes(role)
            ) &&
            pendingRole &&
            hasOAuthParams
          ) {
            /* console.log(` Assigning group for role: ${pendingRole}`); */
            setLoading(true);
            try {
              const assignGroupResponse = await postQuery(
                {
                  target: "assignUserToGroup",
                  Input: {
                    userId: decodedToken.sub,
                    groupName: roleGroupMap[pendingRole],
                    role: pendingRole,
                  },
                },
                "backendops"
              );

              /* console.log(" Group assignment response:", assignGroupResponse); */

              setLoading(false);
              if (assignGroupResponse.success) {
                /* console.log(
                  ` Successfully assigned user to group: ${roleGroupMap[pendingRole]}`
                ); */
                // Clear the pending role
                localStorage.removeItem("pendingOAuthRole");

                // Refresh the token to get updated groups
                await Auth.currentSession();
                const refreshedUser = await Auth.currentAuthenticatedUser();
                const refreshedToken =
                  refreshedUser.signInUserSession.idToken.jwtToken;
                const refreshedDecodedToken = jwtDecode(refreshedToken);
                const refreshedGroups =
                  refreshedDecodedToken["cognito:groups"] || [];

                /* console.log(" Refreshed user groups:", refreshedGroups); */

                // Now proceed with the assigned role
                if (
                  pendingRole === "seller-farmer" ||
                  pendingRole === "global-partner" ||
                  pendingRole === "buyer"
                ) {
                  setLoading(true);
                  const userData = {
                    given_name: refreshedDecodedToken.given_name,
                    family_name: refreshedDecodedToken.family_name,
                    email: refreshedDecodedToken.email,
                    Id: refreshedDecodedToken.sub,
                    role: pendingRole,
                  };

                  const response = await fetchData({
                    target: "checkUserExist",
                    Input: {
                      Id: refreshedDecodedToken.sub,
                      EmailID: refreshedDecodedToken.email,
                    },
                  });

                  setLoading(false);
                  if (response.status === 3) {
                    // Clear session and localStorage for inactive account
                    await clearSessionAndStorage();
                    setModal({
                      show: true,
                      type: "error",
                      title: "Account Inactive",
                      message:
                        "Oops! your account is inactive. Please reach out to your administrator for help.",
                      autoClose: false,
                    });
                  } else if (response.status === 2) {
                    // Clear session and localStorage for duplicate account
                    await clearSessionAndStorage();
                    setModal({
                      show: true,
                      type: "error",
                      title: "Duplicate Account",
                      message: `Oops! another user already exists with email ${refreshedDecodedToken.email}. Please reach out to your administrator for help.`,
                      autoClose: false,
                    });
                  } else if (response.status === 1) {
                    navigate(`/${pendingRole}`);
                  } else {
                    navigate(`/kyc`, { state: { userData } });
                  }
                } else {
                  setLoading(false);
                  navigate(`/${pendingRole}`);
                }
                return;
              } else {
                setLoading(false);
                console.error(
                  " Failed to assign group:",
                  assignGroupResponse.error
                );
                // Clear the pending role on failure
                localStorage.removeItem("pendingOAuthRole");
              }
            } catch (groupError) {
              setLoading(false);
              console.error(" Error assigning group:", groupError);
              localStorage.removeItem("pendingOAuthRole");
            }
          }

          // Clear any pending role if user already has groups
          if (userGroups.length > 0 && pendingRole) {
            localStorage.removeItem("pendingOAuthRole");
          }

          // Find which role this user belongs to
          const userRole = getUserRoleFromGroups(userGroups);

          if (userRole) {
            /* console.log(`User role determined: ${userRole}`); */

            // Special handling for global-partner role
            if (
              userRole === "global-partner" ||
              userRole === "buyer" ||
              userRole === "seller-farmer"
            ) {
              try {
                /* console.log(
                  " Checking if global-partner user exists in system..."
                ); */
                const response = await fetchData({
                  target: "checkUserExist",
                  Input: {
                    Id: decodedToken.sub,
                    EmailID: decodedToken.email,
                    role: userRole,
                  },
                });

                /* console.log(" User existence check response:", response); */
                setLoading(false);
                const userData = {
                  given_name: decodedToken.given_name,
                  family_name: decodedToken.family_name,
                  email: decodedToken.email,
                  Id: decodedToken.sub,
                  role: userRole,
                };

                if (response.status === 3) {
                  // Clear session and localStorage for inactive account
                  await clearSessionAndStorage();
                  setModal({
                    show: true,
                    type: "error",
                    title: "Account Inactive",
                    message:
                      "Oops! your account is inactive. Please reach out to your administrator for help.",
                    autoClose: false,
                  });
                } else if (response.status === 2) {
                  // Clear session and localStorage for duplicate account
                  await clearSessionAndStorage();
                  setModal({
                    show: true,
                    type: "error",
                    title: "Duplicate Account",
                    message: `Oops! another user already exists with email ${decodedToken.email}. Please reach out to your administrator for help.`,
                    autoClose: false,
                  });
                } else if (response.status === 1) {
                  /* console.log(
                    " User exists, redirecting to global-partner dashboard"
                  ); */
                  navigate(
                    `/${userRole}/${
                      userRole === "seller-farmer"
                        ? "dashboard"
                        : "discoverevaluate"
                    }`
                  );
                } else {
                  setLoading(false);
                  /* console.log(" User doesn't exist, redirecting to KYC"); */
                  navigate(`/kyc`, { state: { userData } });
                }
              } catch (error) {
                setLoading(false);
                console.error("Error checking user existence:", error);
                // Fallback to login page
                navigate(`/login/${userRole}`);
              }
            } else {
              setLoading(false);
              // For other roles, redirect directly to their dashboard
              /* console.log(` Redirecting to ${userRole} dashboard`); */
              navigate(`/${userRole}`);
            }
          } else {
            setLoading(false);
            /* console.log(" User has no recognized roles, staying on home page"); */
            // User is authenticated but has no recognized roles
            // Could redirect to a "no access" page or stay on home
          }
        } else {
          /* console.log(" User is not authenticated, staying on home page"); */
        }
      } catch (error) {
        setLoading(false);
        /* console.log(" No authenticated user found:", error?.message || error); */

        // Check if this was an OAuth redirect that failed
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has("error")) {
          console.error(" OAuth error:", urlParams.get("error"));
          console.error(
            " OAuth error description:",
            urlParams.get("error_description")
          );
          localStorage.removeItem("pendingOAuthRole");
        }
        // User is not authenticated, which is fine for the home page
      }
    };

    // Run the authentication check
    checkAuthStatus();

    // Cleanup Hub listener
    return () => {
      if (hubListener) {
        hubListener();
      }
    };
  }, [navigate, setLoading]);

  // Check if environment is production
  const isProduction =
    process.env.REACT_APP_AMPLIFY_ENV === "prod" ||
    process.env.REACT_APP_AMPLIFY_ENV === "production";
  ///* console.log("++++++++++++++ AMPLIFY_ENV:", process.env.REACT_APP_AMPLIFY_ENV);
  return (
    <div className="home-page">
      {isProduction ? (
        <div className="homepage-inblock"></div>
      ) : (
        <div className="homepage-inblock">
          <h1>Welcome to Grains Global</h1>
          <nav>
            <ul className="mb-0">
              <li>
                <Link to="/login/buyer">
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: "#de6b14",
                      color: "white",
                      "&:hover": {
                        border: "1px solid #de6b14",
                        backgroundColor: "#fff",
                        color: "#de6b14",
                        fontWeight: "500",
                      },
                    }}
                  >
                    Buyer Login
                  </Button>
                </Link>
              </li>
              <li>
                <Link to="/login/global-partner">
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: "#de6b14",
                      color: "white",
                      "&:hover": {
                        border: "1px solid #de6b14",
                        backgroundColor: "#fff",
                        color: "#de6b14",
                        fontWeight: "500",
                      },
                    }}
                  >
                    Trading Partner Login
                  </Button>
                </Link>
              </li>
              <li>
                <Link to="/login/finance-partner">
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: "#de6b14",
                      color: "white",
                      "&:hover": {
                        border: "1px solid #de6b14",
                        backgroundColor: "#fff",
                        color: "#de6b14",
                        fontWeight: "500",
                      },
                    }}
                  >
                    Finance Partner Login
                  </Button>
                </Link>
              </li>
              <li>
                <Link to="/login/amtpl">
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: "#de6b14",
                      color: "white",
                      "&:hover": {
                        border: "1px solid #de6b14",
                        backgroundColor: "#fff",
                        color: "#de6b14",
                        fontWeight: "500",
                      },
                    }}
                  >
                    AMTPL Admin Login
                  </Button>
                </Link>
              </li>
              <li>
                <Link to="/login/seller-farmer">
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: "#de6b14",
                      color: "white",
                      "&:hover": {
                        border: "1px solid #de6b14",
                        backgroundColor: "#fff",
                        color: "#de6b14",
                        fontWeight: "500",
                      },
                    }}
                  >
                    Seller & Farmer Login
                  </Button>
                </Link>
              </li>
              <li>
                <Link to="/login/agropreneur">
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: "#de6b14",
                      color: "white",
                      "&:hover": {
                        border: "1px solid #de6b14",
                        backgroundColor: "#fff",
                        color: "#de6b14",
                        fontWeight: "500",
                      },
                    }}
                  >
                    AgroPreneur Login
                  </Button>
                </Link>
              </li>
            </ul>
          </nav>
        </div>
      )}
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        buttonText="OK"
        autoClose={modal.autoClose}
      />
    </div>
  );
};

export default Home;
